import React, { memo, useMemo } from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
  LabelList,
} from 'recharts';
import { areEqual } from 'Utils/equalityChecks';

import type { CostCategory } from '../types';
import { COST_CATEGORY_LABELS } from '../types';
import { formatCurrency } from '../format';
import classes from '../jobCosting.module.css';

interface Props {
  costsByCategory: Partial<Record<CostCategory, number>>;
}

const CATEGORY_COLORS: Record<string, string> = {
  labour: '#2563eb',
  materials: '#f59e0b',
  subcontractor: '#8b5cf6',
  equipment: '#10b981',
  permits: '#ef4444',
  other_direct: '#6b7280',
};

/** Horizontal bar of cost grouped by category for a single job. */
function CostBreakdownChart({ costsByCategory }: Props) {
  const data = useMemo(
    () =>
      Object.entries(costsByCategory)
        .map(([category, amount]) => ({
          category,
          label: COST_CATEGORY_LABELS[category as CostCategory] || category,
          amount: Math.round(Number(amount) || 0),
        }))
        .filter((d) => d.amount > 0)
        .sort((a, b) => b.amount - a.amount),
    [costsByCategory]
  );

  const total = useMemo(() => data.reduce((sum, d) => sum + d.amount, 0), [data]);

  if (data.length === 0) return null;

  return (
    <div className={classes.chartCard}>
      <div className={classes.chartTitle}>Cost by category</div>
      <ResponsiveContainer width="100%" height={Math.max(180, data.length * 52)}>
        <BarChart
          data={data}
          layout="vertical"
          barCategoryGap="28%"
          margin={{ top: 8, right: 72, left: 8, bottom: 8 }}
        >
          <CartesianGrid strokeDasharray="2 4" stroke="#eef0f4" horizontal={false} />
          <XAxis
            type="number"
            tickFormatter={(v) => formatCurrency(Number(v))}
            tick={{ fontSize: 11, fill: '#9aa1ac' }}
            axisLine={false}
            tickLine={false}
          />
          <YAxis
            type="category"
            dataKey="label"
            tick={{ fontSize: 12, fill: '#4b5563' }}
            width={110}
            axisLine={false}
            tickLine={false}
          />
          <Tooltip
            cursor={{ fill: 'rgba(99, 102, 241, 0.06)' }}
            formatter={(v) => [
              `${formatCurrency(Number(v))}${total > 0 ? ` · ${Math.round((Number(v) / total) * 100)}%` : ''}`,
              'Cost',
            ]}
            contentStyle={{
              borderRadius: 8,
              border: '1px solid #e5e7eb',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
              fontSize: 12,
            }}
          />
          <Bar dataKey="amount" radius={[4, 4, 4, 4]} barSize={18} animationDuration={600}>
            {data.map((d) => (
              <Cell key={d.category} fill={CATEGORY_COLORS[d.category] || '#6b7280'} />
            ))}
            <LabelList
              dataKey="amount"
              position="right"
              formatter={(v: number) => formatCurrency(Number(v))}
              style={{ fontSize: 11, fontWeight: 600, fill: '#374151' }}
            />
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

const CostBreakdownChartMemo = memo(CostBreakdownChart, areEqual);

export { CostBreakdownChartMemo as CostBreakdownChart };
